import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6k3c12gaju8afbcej10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="px-10 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="https://public.readdy.ai/ai/img_res/1e0fced4-bd51-4226-98eb-c9b41885637b.png" alt="MAGIC d.o.o. Logo" className="h-10 w-10 object-contain" />
              <span className={`text-2xl font-bold tracking-wider ${isScrolled ? 'text-gray-900' : 'text-white'}`}>MAGIC d.o.o.</span>
            </div>
            <div className="flex gap-8">
              <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Home</a>
              <a href="#products" className={`text-sm font-medium transition-colors cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Products</a>
              <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>About</a>
              <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#3d2e7c] to-[#0f1729]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative z-10 text-center px-8 max-w-4xl mx-auto">
          <h1 className="text-7xl font-extrabold text-white mb-4 leading-tight">Premium iOS Applications</h1>
          <h2 className="text-6xl font-serif italic text-white/90 mb-12 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Built for Modern Users</h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">Developing cutting-edge VPN and Cleaner solutions that prioritize security, performance, and seamless user experience</p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex justify-between items-end mb-20">
            <h2 className="text-6xl font-bold text-gray-900">Our Products</h2>
            <p className="text-lg text-gray-600 max-w-md text-right">Innovative solutions designed to protect and optimize your iOS devices</p>
          </div>

          <div className="grid grid-cols-2 gap-10">
            {/* VPN Card */}
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-lg p-12 hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center justify-center mb-10">
                <div className="w-72 h-72 flex items-center justify-center">
                  <img src="https://public.readdy.ai/ai/img_res/54121e404b393e2767e0ab983ec273f7.jpg" alt="VPN Security" className="w-full h-full object-contain" />
                </div>
              </div>
              <h3 className="text-5xl font-bold text-gray-900 mb-8">VPN</h3>
              <p className="text-base text-gray-600 leading-relaxed">Enterprise-grade encryption protecting your privacy across all networks with lightning-fast connection speeds</p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-gradient-to-br from-white to-green-50 rounded-3xl shadow-lg p-12 hover:shadow-2xl transition-shadow duration-300">
              <div className="flex items-center justify-center mb-10">
                <div className="w-72 h-72 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=minimalist%20geometric%20broom%20and%20sparkle%20icon%20representing%20phone%20cleaner%20and%20optimization%20in%20modern%20flat%20design%20style%20with%20gradient%20green%20and%20mint%20colors%20on%20clean%20white%20background%20professional%20tech%20illustration&width=280&height=280&seq=cleaner-icon-002&orientation=squarish" alt="Cleaner Optimization" className="w-full h-full object-contain" />
                </div>
              </div>
              <h3 className="text-5xl font-bold text-gray-900 mb-8">Cleaner</h3>
              <p className="text-base text-gray-600 leading-relaxed">Intelligent storage optimization removing junk files, duplicate photos, and unnecessary data to keep your device running smoothly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-36 bg-[#1c1c1e]">
        <div className="max-w-7xl mx-auto px-20">
          <div className="grid grid-cols-10 gap-8">
            {/* Security Feature */}
            <div className="col-span-4 bg-white/5 rounded-2xl p-10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <div className="mb-8">
                <div className="w-full h-80 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=abstract%20glowing%20lock%20and%20shield%20visualization%20with%20digital%20encryption%20patterns%20representing%20military%20grade%20security%20in%20dark%20background%20with%20blue%20and%20cyan%20neon%20glow%20effects%20modern%20tech%20illustration&width=320&height=320&seq=security-feature-003&orientation=squarish" alt="Military-Grade Security" className="w-full h-full object-cover rounded-xl" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-white mb-6">Military-Grade Security</h3>
              <p className="text-base text-gray-400 leading-relaxed">AES-256 encryption standard ensuring your data remains completely private and protected from any threats</p>
            </div>

            {/* Performance Feature */}
            <div className="col-span-3 bg-white/5 rounded-2xl p-10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <h3 className="text-2xl font-bold text-white text-center mb-8">Lightning Performance</h3>
              <div className="flex items-center justify-center">
                <div className="w-full h-72 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=minimalist%20speedometer%20gauge%20with%20gradient%20colors%20showing%20maximum%20performance%20and%20speed%20in%20dark%20background%20modern%20tech%20illustration%20with%20glowing%20effects&width=280&height=280&seq=performance-feature-004&orientation=squarish" alt="Lightning Performance" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>

            {/* User Experience Feature */}
            <div className="col-span-3 bg-white/5 rounded-2xl p-10 backdrop-blur-sm hover:bg-white/10 transition-colors duration-300">
              <h3 className="text-2xl font-bold text-white text-center mb-8">Intuitive Interface</h3>
              <div className="flex items-center justify-center">
                <div className="w-full h-72 flex items-center justify-center">
                  <img src="https://readdy.ai/api/search-image?query=clean%20modern%20iOS%20mobile%20app%20interface%20mockup%20showing%20smooth%20rounded%20corners%20and%20elegant%20gradient%20design%20elements%20in%20dark%20background%20professional%20UI%20illustration&width=280&height=280&seq=interface-feature-005&orientation=squarish" alt="Intuitive Interface" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-40 bg-white">
        <div className="max-w-7xl mx-auto px-20">
          <div className="grid grid-cols-10 gap-16">
            {/* Left Side - Image */}
            <div className="col-span-4">
              <div className="inline-block border border-gray-300 rounded-full px-6 py-3 mb-12">
                <span className="text-xs uppercase tracking-widest text-gray-600">About MAGIC d.o.o.</span>
              </div>
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <div className="w-full h-96">
                  <img src="https://readdy.ai/api/search-image?query=modern%20tech%20team%20collaboration%20in%20bright%20office%20environment%20with%20developers%20working%20on%20iOS%20applications%20showing%20teamwork%20and%20innovation%20with%20clean%20professional%20atmosphere%20and%20natural%20lighting&width=600&height=450&seq=about-team-006&orientation=landscape" alt="MAGIC d.o.o. Team" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            {/* Right Side - Content */}
            <div className="col-span-6 flex flex-col justify-center">
              <h2 className="text-6xl font-extrabold text-gray-900 leading-tight mb-8">
                <div>Expert Team</div>
                <div>Innovative Solutions</div>
                <div>Global Impact</div>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
                MAGIC d.o.o. brings together talented developers and designers passionate about creating exceptional iOS applications. Our VPN and Cleaner products reflect our commitment to user privacy, device optimization, and delivering premium mobile experiences that users trust worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-20">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-bold text-gray-900 mb-6">Get in Touch</h2>
            <p className="text-xl text-gray-600">We'd love to hear from you</p>
          </div>

          <div className="grid grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-lg p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-8">Send us a message</h3>
              <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                      placeholder="Your message (max 500 characters)"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information */}
            <div className="flex flex-col justify-center space-y-10">
              <div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Address</h4>
                    <p className="text-base text-gray-800">Zuranj bb</p>
                    <p className="text-base text-gray-800">Citluk, 88260</p>
                    <p className="text-base text-gray-800">Bosnia & Herzegovina</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-mail-line text-2xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Email</h4>
                    <p className="text-base text-gray-800">contact@magicdoo.com</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Number</h4>
                    <p className="text-base text-gray-800">+1 6206440201</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-20 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-baseline gap-2 mb-8">
                <img src="https://public.readdy.ai/ai/img_res/1e0fced4-bd51-4226-98eb-c9b41885637b.png" alt="MAGIC d.o.o. Logo" className="h-16 w-16 object-contain" />
                <span className="text-7xl font-extrabold text-gray-900">MAGIC</span>
                <span className="text-7xl font-light italic text-gray-500">d.o.o.</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-32 h-1 bg-gray-900"></div>
                <span className="text-sm text-gray-600">Crafting Premium iOS Experiences</span>
              </div>
            </div>

            <div className="text-right">
              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Address</h4>
                <p className="text-base text-gray-800">Zuranj bb</p>
                <p className="text-base text-gray-800">Citluk, 88260</p>
                <p className="text-base text-gray-800">Bosnia & Herzegovina</p>
              </div>

              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Email</h4>
                <p className="text-base text-gray-800">contact@magicdoo.com</p>
              </div>

              <div className="mb-8">
                <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Number</h4>
                <p className="text-base text-gray-800">+1 6206440201</p>
              </div>

              <p className="text-sm text-gray-500 mb-6">© 2025 MAGIC d.o.o.</p>
              
              <div className="flex justify-end gap-5">
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl text-gray-700"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-xl text-gray-700"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
                  <i className="ri-github-fill text-xl text-gray-700"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}